// Words and formatting for the statutory foundations (CD-S1): government numbers, business registrations,
// statutory payroll facts and the government rules Apparify uses. Nothing here calculates a contribution or a tax.

export const EMPLOYEE_NUMBER_LABELS = {
  SSS: { name: "SSS", label: "SSS number", help: "Social Security System member number." },
  PHILHEALTH: { name: "PhilHealth", label: "PhilHealth number", help: "PhilHealth identification number." },
  PAGIBIG: { name: "Pag-IBIG", label: "Pag-IBIG MID number", help: "Pag-IBIG Fund membership ID." },
  TIN: { name: "TIN", label: "TIN", help: "Bureau of Internal Revenue taxpayer identification number." },
};

export const EMPLOYER_NUMBER_LABELS = {
  SSS: { name: "SSS", label: "SSS employer number", help: "The business's employer number with the Social Security System." },
  PHILHEALTH: { name: "PhilHealth", label: "PhilHealth employer number", help: "The business's PhilHealth employer number." },
  PAGIBIG: { name: "Pag-IBIG", label: "Pag-IBIG employer number", help: "The business's Pag-IBIG Fund employer ID." },
  BIR: { name: "BIR", label: "Business TIN", help: "The business's taxpayer identification number." },
};

export const AGENCY_NAMES = {
  SSS: "SSS",
  PHILHEALTH: "PhilHealth",
  PAGIBIG: "Pag-IBIG",
  BIR_WHT: "Withholding tax (BIR)",
};

// "Recorded" means HR typed a number here. It does not mean the agency has confirmed a registration.
export const numberStatus = (item) => (item?.recorded ? "Recorded" : "Missing");

export const FACT_TYPES = ["MWE_STATUS", "PHILHEALTH_MBS", "PRIOR_EMPLOYER_IN_YEAR"];

export const FACT_LABELS = {
  MWE_STATUS: {
    title: "Minimum wage earner status",
    help: "Whether this employee is paid the minimum wage. Used later to decide how withholding tax is reviewed.",
    action: "Record status",
  },
  PHILHEALTH_MBS: {
    title: "PhilHealth monthly basic salary basis",
    help: "Used only for PhilHealth contribution calculation. This does not change the employee's pay.",
    action: "Record basis",
  },
  PRIOR_EMPLOYER_IN_YEAR: {
    title: "Previous employer this year",
    help: "Whether the employee worked for another employer earlier in the same calendar year.",
    action: "Record answer",
  },
};

export const PHILHEALTH_BASIS_METHODS = [
  { value: "HR_DECLARED", label: "Entered by HR" },
  { value: "EEMR_395", label: "Daily rate × 395 ÷ 12 (works every day, including rest days and holidays)" },
  { value: "EEMR_313", label: "Daily rate × 313 ÷ 12 (no work and no pay on rest days)" },
  { value: "EEMR_305", label: "Daily rate × 305 ÷ 12 (as 313, special days not paid)" },
  { value: "EEMR_261", label: "Daily rate × 261 ÷ 12 (no work and no pay on Saturdays and Sundays)" },
  { value: "EEMR_253", label: "Daily rate × 253 ÷ 12 (as 261, special days not paid)" },
];

const FACT_STATE_LABELS = { current: "Current", upcoming: "Starts later", past: "Ended", cancelled: "Cancelled" };
export const factStateLabel = (state) => FACT_STATE_LABELS[state] || "";

const peso = new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" });
export const formatPesoCents = (cents) => peso.format(Number(cents || 0) / 100);

export function formatDate(value) {
  if (!value) return "";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

/** The calendar day of a moment in time, as the person looking at the screen experiences it (their time zone). */
export function formatLocalDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
}

export function formatMonth(value) {
  if (!value) return "";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-PH", { year: "numeric", month: "long", timeZone: "UTC" });
}

/** What a recorded fact says, in words. */
export function factValueText(fact) {
  if (!fact) return "";
  if (fact.factType === "MWE_STATUS") return fact.value ? "Minimum wage earner" : "Not a minimum wage earner";
  if (fact.factType === "PHILHEALTH_MBS") return `${formatPesoCents(fact.amountCents)} per month`;
  if (fact.factType === "PRIOR_EMPLOYER_IN_YEAR") {
    return fact.value ? `Had another employer in ${fact.calendarYear}` : `No other employer in ${fact.calendarYear}`;
  }
  return "";
}

export function factPeriodText(fact) {
  if (!fact) return "";
  if (fact.factType === "PRIOR_EMPLOYER_IN_YEAR") return `Calendar year ${fact.calendarYear}`;
  const from = formatDate(fact.effectiveFrom);
  return fact.effectiveUntil ? `${from} to ${formatDate(fact.effectiveUntil)}` : `From ${from}`;
}

export const basisMethodLabel = (value) => PHILHEALTH_BASIS_METHODS.find((item) => item.value === value)?.label || "";

/** The fact in effect now for each type, plus everything else as history. */
export function groupFacts(facts = []) {
  return FACT_TYPES.map((factType) => {
    const rows = facts.filter((fact) => fact.factType === factType);
    const current = rows.find((fact) => fact.state === "current") || null;
    const upcoming = rows.filter((fact) => fact.state === "upcoming");
    const history = rows.filter((fact) => fact !== current && fact.state !== "upcoming");
    // Only the newest record that is still in force (or scheduled) can be cancelled; the server enforces it too.
    const newest = rows.find((fact) => fact.state !== "cancelled") || null;
    return { factType, current, upcoming, history, cancellableId: newest?.id ?? null };
  });
}

/* ── Government rules ─────────────────────────────────────────────────────────────────────── */

export const VERIFICATION_LABELS = {
  ISSUING_AGENCY: {
    label: "Issuing-agency verified",
    explain: "Read in the agency's own published document.",
  },
  OFFICIAL_GOVERNMENT_RECORD: {
    label: "Official government record",
    explain: "Read in the official copy filed with the national administrative register.",
  },
  GOVERNMENT_CORROBORATED: {
    label: "Government-corroborated",
    explain: "Confirmed through an official government report of the agency's announcement. The agency's own numbered document has not been published.",
  },
};

export const verificationLabel = (level) => VERIFICATION_LABELS[level]?.label || "";

const RULE_STATUS_LABELS = { in_use: "In use", past: "Earlier period", future: "Not yet in effect" };
export const ruleStatusLabel = (status) => RULE_STATUS_LABELS[status] || "";

export function rulePeriodText(rule) {
  if (!rule) return "";
  const monthly = rule.dateBasis === "CONTRIBUTION_MONTH";
  const from = monthly ? formatMonth(rule.effectiveFrom) : formatDate(rule.effectiveFrom);
  const basis = monthly ? "Contribution months" : "Pay dates";
  if (!rule.effectiveUntil) return `${basis} from ${from}`;
  const until = monthly ? formatMonth(rule.effectiveUntil) : formatDate(rule.effectiveUntil);
  return `${basis} from ${from} to ${until}`;
}

const percent = (basisPoints) => `${(Number(basisPoints) / 100).toLocaleString("en-PH", { maximumFractionDigits: 2 })}%`;

/** Plain-language facts of a rule, as label / value pairs. Read from the rule data; nothing is computed here. */
export function ruleFacts(rule) {
  const details = rule?.details;
  if (!details) return [];
  if (details.kind === "SSS_BRACKETS") {
    return [
      { label: "Contribution table", value: `${details.bracketCount} salary brackets` },
      { label: "Monthly salary credit", value: `${formatPesoCents(details.minMscCents)} to ${formatPesoCents(details.maxMscCents)}` },
      { label: "Shares", value: "Employee 5%, employer 10%, plus the employer-paid EC contribution" },
    ];
  }
  if (details.kind === "RATES") {
    const facts = [];
    if (details.totalRateBp) {
      facts.push({ label: "Premium rate", value: `${percent(details.totalRateBp)} of monthly basic salary, shared equally` });
    } else {
      facts.push({ label: "Employee rate", value: details.lowIncomeEmployeeRateBp
        ? `${percent(details.employeeRateBp)} (${percent(details.lowIncomeEmployeeRateBp)} at ${formatPesoCents(details.lowIncomeThresholdCents)} and below)`
        : percent(details.employeeRateBp) });
      facts.push({ label: "Employer rate", value: percent(details.employerRateBp) });
    }
    if (details.floorCents) facts.push({ label: "Lowest salary used", value: formatPesoCents(details.floorCents) });
    if (details.ceilingCents) {
      facts.push({ label: details.totalRateBp ? "Highest salary used" : "Maximum fund salary", value: formatPesoCents(details.ceilingCents) });
    }
    facts.push({
      label: "Rounding",
      value: details.sourceRounding
        ? "Set by the agency: the premium is rounded to centavos, then split; an odd centavo stays with the employer."
        : "The agency publishes no rounding rule.",
    });
    return facts;
  }
  if (details.kind === "WITHHOLDING_TABLES") {
    const names = { monthly: "monthly", semi_monthly: "semi-monthly", weekly: "weekly", daily: "daily" };
    const recorded = Object.keys(details.tables || {}).map((key) => names[key] || key);
    return [
      { label: "Tables recorded", value: recorded.join(", ") },
      { label: "Not recorded", value: "Daily and bi-weekly payroll. Withholding for these is reviewed by hand." },
    ];
  }
  return [];
}
